(() => {
  const fold = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('es').trim();
  const slug = value => fold(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const format = (message, values) => String(message || '').replace(/\[\[(\w+)\]\]/g, (_, key) => String(values[key] ?? ''));
  const node = (tag, text, className) => {
    const element = document.createElement(tag);
    if (text !== undefined) element.textContent = text;
    if (className) element.className = className;
    return element;
  };
  const icon = name => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'uni-icon uni-icon--' + name);
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '24'); svg.setAttribute('height', '24');
    svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false'); svg.setAttribute('fill', 'none');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', ({ down: 'm6 9 6 6 6-6', close: 'm6 6 12 12M6 18 18 6', check: 'm5 12 4 4L19 6', plus: 'M12 5v14M5 12h14' })[name]);
    path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '1.7');
    path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round');
    svg.append(path); return svg;
  };
  const storageGet = key => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return null; } };
  const storageSet = (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {} };
  const RETURN_KEY = 'unisystem:catalog:return:v1';
  const returnContext = () => {
    const saved = storageGet(RETURN_KEY);
    if (!saved || !Number.isFinite(saved.createdAt) || Date.now() - saved.createdAt > 4 * 60 * 60 * 1000 || saved.createdAt > Date.now() + 10000) return null;
    try {
      const url = new URL(saved.href, location.origin);
      if (url.origin !== location.origin || !/^\/(?:pages|collections)\//.test(url.pathname)) return null;
      const clean = new URL(url.pathname, location.origin);
      for (const key of ['q', 'area', 'modalidad']) if (url.searchParams.get(key)) clean.searchParams.set(key, url.searchParams.get(key).slice(0, 120));
      const preview = url.searchParams.get('preview_theme_id');
      if (/^\d+$/.test(preview || '')) clean.searchParams.set('preview_theme_id', preview);
      if (/^[a-zA-Z0-9_-]{1,240}$/.test(saved.targetId || '')) clean.hash = saved.targetId;
      return { ...saved, href: clean.pathname + clean.search + clean.hash };
    } catch { return null; }
  };
  const restoreReturnLinks = (scope = document) => {
    const saved = returnContext();
    if (!saved) return;
    scope.querySelectorAll('[data-catalog-return]').forEach(link => {
      link.href = saved.href;
      if (link.dataset.returnLabel) link.textContent = link.dataset.returnLabel;
    });
  };

  class UniCatalog extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.cards = Array.from(this.querySelectorAll('[data-program-card]'));
      this.programs = new Map();
      this.animations = new Set();
      this.cards.forEach(card => {
        const data = card.dataset;
        if (!data.programId || !data.programUrl) return;
        this.programs.set(data.programId, { id: data.programId, name: data.programName, shortName: data.programShortName, area: data.programArea, url: data.programUrl, duration: data.programDuration, modality: data.programModality, summary: data.programSummary, occupation: data.programOccupation, requirements: data.programRequirements, skills: data.programSkills });
        card.dataset.searchText = fold([data.programName, data.programShortName, data.programArea, data.programSummary, data.programSkills, data.programModality, data.programDuration].join(' '));
      });
      this.search = this.querySelector('[data-catalog-search]');
      this.modality = this.querySelector('[data-catalog-modality]');
      this.modalityButtons = Array.from(this.querySelectorAll('[data-catalog-modality-button]'));
      this.listen(this.modality, 'change', () => this.filter());
      this.initialArea = this.dataset.initialArea || '';
      this.area = this.initialArea;
      this.selected = [];
      this.pair = [];
      this.media = window.matchMedia('(max-width: 767px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.limit = 3;
      this.dialog = this.querySelector('[data-compare-dialog]');
      this.comparisonEnabled = !!this.dialog && typeof this.dialog.showModal === 'function';
      this.storageKey = 'unisystem:programme-comparison:v1:' + (fold(this.initialArea) || 'all');
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this, 'change', event => {
        const select = event.target.closest('[data-compare-slot]');
        if (select) this.changePair(Number(select.dataset.compareSlot), select.value);
      });
      this.listen(this.search, 'input', () => {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => this.filter(), 120);
      });
      this.listen(this.querySelector('[data-catalog-form]'), 'submit', event => { event.preventDefault(); window.clearTimeout(this.searchTimer); this.filter(); });
      this.listen(window, 'popstate', () => { this.readUrlState(); this.filter({ animate: false, syncUrl: false }); });
      this.listen(window, 'pagehide', () => this.closeDialog(true));
      this.listen(document, 'uni:motionchange', () => this.cancelAnimations());
      this.listen(this.reduced, 'change', () => this.cancelAnimations());
      if (this.cards.length) {
        this.readUrlState();
        this.renderAreas();
        this.querySelector('[data-catalog-tools]').hidden = false;
        this.filter({ animate: false, syncUrl: false });
        this.restorePosition();
      }
      if (this.comparisonEnabled) {
        this.restoreSelection();
        this.querySelectorAll('[data-compare-toggle]').forEach(button => { button.hidden = false; });
        this.listen(this.dialog, 'close', () => {
          if (this.isConnected && this.opener?.isConnected && !this.opener.disabled) this.opener.focus({ preventScroll: true });
          else if (this.isConnected) this.restoreCardFocus(this.selected[0]);
        });
        this.listen(this.dialog, 'cancel', event => {
          if (window.UniMotionPanels?.close) { event.preventDefault(); this.closeDialog(); }
        });
        this.listen(this.dialog, 'click', event => {
          if (event.target !== this.dialog) return;
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closeDialog();
        });
        this.listen(this.media, 'change', () => this.updateSelection());
        this.updateSelection();
      }
    }

    disconnectedCallback() {
      this.closeDialog(true);
      this.controller?.abort();
      this.controller = null;
      this.cancelAnimations();
      window.clearTimeout(this.searchTimer);
    }

    listen(target, type, callback) { target?.addEventListener(type, callback, { signal: this.signal }); }

    readUrlState() {
      try {
        const params = new URL(location.href).searchParams;
        const areas = Array.from(new Set(Array.from(this.programs.values()).map(program => program.area).filter(Boolean)));
        this.area = this.initialArea || areas.find(area => slug(area) === params.get('area')) || '';
        if (this.search) this.search.value = String(params.get('q') || '').slice(0, 120);
        if (this.modality) this.modality.value = ['distancia','presencial'].includes(params.get('modalidad')) ? params.get('modalidad') : '';
      } catch { this.area = this.initialArea; }
    }

    syncUrl() {
      try {
        const url = new URL(location.href);
        const query = String(this.search?.value || '').trim().slice(0, 120);
        if (query) url.searchParams.set('q', query); else url.searchParams.delete('q');
        if (this.area) url.searchParams.set('area', slug(this.area)); else url.searchParams.delete('area');
        if (this.modality?.value) url.searchParams.set('modalidad', this.modality.value); else url.searchParams.delete('modalidad');
        if (url.hash.startsWith('#ProgramCard-')) url.hash = '';
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      } catch {}
    }

    rememberResults(id) {
      const card = this.cards.find(item => item.dataset.programId === id);
      if (!card) return;
      try {
        const current = new URL(location.href);
        if (!/^\/(?:pages|collections)\//.test(current.pathname)) return;
        const url = new URL(current.pathname, current.origin);
        const query = String(this.search?.value || '').trim().slice(0, 120);
        if (query) url.searchParams.set('q', query);
        if (this.area) url.searchParams.set('area', slug(this.area));
        if (this.modality?.value) url.searchParams.set('modalidad', this.modality.value);
        const preview = current.searchParams.get('preview_theme_id');
        if (/^\d+$/.test(preview || '')) url.searchParams.set('preview_theme_id', preview);
        url.hash = card.id;
        storageSet(RETURN_KEY, { href: url.pathname + url.search + url.hash, targetId: card.id, programId: id, scrollY: Math.max(0, Math.round(window.scrollY)), createdAt: Date.now() });
      } catch {}
    }

    restorePosition() {
      const saved = returnContext();
      if (!saved) return;
      let target;
      try {
        const storedUrl = new URL(saved.href, location.origin);
        if (storedUrl.pathname !== location.pathname || decodeURIComponent(location.hash.slice(1)) !== saved.targetId) return;
        target = this.cards.find(card => card.id === saved.targetId);
      } catch { return; }
      if (!target) return;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        window.scrollTo({ top: Math.max(0, Number(saved.scrollY) || 0), behavior: 'instant' });
        (target.hidden ? this.search : target.querySelector('.uni-program-card__title a'))?.focus({ preventScroll: true });
      }));
    }

    renderAreas() {
      const container = this.querySelector('[data-catalog-areas]');
      if (!container) return;
      const areas = new Map();
      this.programs.forEach(program => { if (program.area) areas.set(program.area, (areas.get(program.area) || 0) + 1); });
      const fragment = document.createDocumentFragment();
      const sorted = Array.from(areas).sort((a, b) => a[0].localeCompare(b[0], 'es'));
      const choices = this.initialArea ? sorted : [['', this.programs.size], ...sorted];
      choices.forEach(([area, count]) => {
        const button = node('button', undefined, 'uni-catalog__area');
        button.type = 'button';
        button.dataset.catalogArea = area;
        button.setAttribute('aria-pressed', String(area === this.area));
        button.append(node('span', area || this.dataset.allAreas), node('span', String(count), 'uni-catalog__area-count'));
        fragment.append(button);
      });
      container.replaceChildren(fragment);
    }

    onClick(event) {
      const link = event.target.closest('[data-program-link]');
      if (link && this.contains(link)) {
        this.rememberResults(link.dataset.programLink);
        if (this.dialog?.contains(link)) this.closeDialog(true);
        return;
      }
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-catalog-modality-button')) {
        const modality = button.dataset.catalogModalityButton;
        if (this.modality && ['presencial', 'distancia'].includes(modality)) {
          this.modality.value = modality;
          this.filter();
        }
      }
      else if (button.hasAttribute('data-catalog-area')) { this.area = button.dataset.catalogArea; this.filter(); }
      else if (button.hasAttribute('data-catalog-reset')) { this.search.value = ''; if (this.modality) this.modality.value = ''; this.area = this.initialArea; this.filter(); this.search.focus({ preventScroll: true }); }
      else if (button.hasAttribute('data-compare-toggle')) {
        const card = button.closest('[data-program-card]');
        if (card) this.toggle(card.dataset.programId);
      } else if (button.hasAttribute('data-compare-remove')) {
        const id = button.dataset.compareRemove;
        const wasDialog = this.dialog?.contains(button);
        this.selected = this.selected.filter(item => item !== id);
        this.updateSelection();
        if (wasDialog && this.dialog.open) this.dialog.querySelector('[data-compare-close]').focus({ preventScroll: true });
        else if (!wasDialog) this.restoreCardFocus(id);
      } else if (button.hasAttribute('data-compare-clear')) {
        const first = this.selected[0]; this.selected = []; this.updateSelection(); this.restoreCardFocus(first);
      } else if (button.hasAttribute('data-compare-expand')) this.setExpanded(button.getAttribute('aria-expanded') !== 'true');
      else if (button.hasAttribute('data-compare-open')) {
        if (this.selected.length < 2) return;
        this.opener = button;
        this.renderPairControls();
        this.renderComparison();
        if (window.UniMotionPanels?.open) window.UniMotionPanels.open(this.dialog);
        else this.dialog.showModal();
      } else if (button.hasAttribute('data-compare-close')) this.closeDialog();
    }

    closeDialog(immediate = false) {
      if (!this.dialog?.open) return;
      if (window.UniMotionPanels?.close) window.UniMotionPanels.close(this.dialog, { immediate });
      else this.dialog.close();
    }

    cancelAnimations() {
      this.animations?.forEach(animation => animation.cancel());
      this.animations?.clear();
    }

    filter(options = {}) {
      const motion = options.animate !== false && !this.reduced?.matches && document.body.dataset.motion !== 'none' && document.body.dataset.motionUser !== 'none' && !this.closest('[data-motion="none"]') && (typeof window.UniMotion?.allowed !== 'function' || window.UniMotion.allowed(this));
      this.cancelAnimations();
      const previous = new Map();
      if (motion) this.cards.filter(card => !card.hidden).forEach(card => previous.set(card, card.getBoundingClientRect()));
      const tokens = fold(this.search?.value).split(/\s+/).filter(Boolean);
      let visible = 0;
      this.cards.forEach(card => {
        card.hidden = Boolean(this.area && card.dataset.programArea !== this.area) || !tokens.every(token => card.dataset.searchText.includes(token)) || (this.modality?.value === 'distancia' && card.dataset.dual !== 'true' && card.dataset.distanceOnly !== 'true') || (this.modality?.value === 'presencial' && card.dataset.distanceOnly === 'true');
        if (card.dataset.dual === 'true' || card.dataset.distanceOnly === 'true') {
          try {
            const target = new URL(card.dataset.programUrl, location.href);
            if (this.modality?.value) target.searchParams.set('modalidad', this.modality.value); else target.searchParams.delete('modalidad');
            const address = target.pathname + target.search + target.hash;
            card.querySelectorAll('a[href]').forEach(link => { const url = new URL(link.href, location.href); if (url.pathname === target.pathname) { if (this.modality?.value) url.searchParams.set('modalidad', this.modality.value); else url.searchParams.delete('modalidad'); link.href = url.pathname + url.search + url.hash; } });
            card.dataset.programUrl = address;
            const entry = this.programs.get(card.dataset.programId); if (entry) entry.url = address;
          } catch {}
        }

        if (!card.hidden) visible++;
      });
      this.querySelectorAll('[data-catalog-area]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.catalogArea === this.area)));
      this.modalityButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.catalogModalityButton === this.modality?.value)));
      const count = this.querySelector('[data-catalog-count]');
      if (count) count.textContent = format(visible === 1 ? this.dataset.countOne : this.dataset.countMany, { count: visible });
      const empty = this.querySelector('[data-catalog-empty]');
      if (empty) empty.hidden = visible > 0 || this.cards.length === 0;
      const reset = this.querySelector('.uni-catalog__results-header [data-catalog-reset]');
      if (reset) reset.hidden = this.area === this.initialArea && !tokens.length && !this.modality?.value;
      if (motion) this.cards.filter(card => !card.hidden && typeof card.animate === 'function').forEach(card => {
        const after = card.getBoundingClientRect(), before = previous.get(card);
        if (after.top > window.innerHeight || after.bottom < 0) return;
        let frames;
        if (before) {
          const dx = before.left - after.left, dy = before.top - after.top;
          if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
          frames = [{ translate: dx + 'px ' + dy + 'px' }, { translate: '0px 0px' }];
        } else frames = [{ opacity: 0 }, { opacity: 1 }];
        const animation = card.animate(frames, { duration: 200, easing: 'cubic-bezier(.2,.7,.2,1)' });
        this.animations.add(animation);
        animation.finished.then(() => this.animations.delete(animation), () => this.animations.delete(animation));
      });
      if (options.syncUrl !== false) this.syncUrl();
      this.dispatchEvent(new CustomEvent('uni:catalog-filter'));
    }

    toggle(id) {
      if (!this.comparisonEnabled || !this.programs.has(id)) return;
      const feedback = this.querySelector('[data-compare-feedback]');
      if (feedback) feedback.hidden = true;
      if (this.selected.includes(id)) this.selected = this.selected.filter(item => item !== id);
      else if (this.selected.length < this.limit) this.selected.push(id);
      else {
        const message = format(this.dataset.limit, { limit: this.limit });
        if (feedback) { feedback.textContent = message; feedback.hidden = false; }
        this.setExpanded(true);
        this.announce(message);
        return;
      }
      this.updateSelection();
    }

    syncPair() { this.pair = Array.from(new Set([...(this.pair || []), ...this.selected])).filter(id => this.selected.includes(id)).slice(0, 2); }

    restoreSelection() {
      const saved = storageGet(this.storageKey);
      this.selected = Array.isArray(saved) ? Array.from(new Set(saved)).filter(id => typeof id === 'string' && this.programs.has(id)).slice(0, 3) : [];
      const savedPair = storageGet(this.storageKey + ':pair');
      this.pair = Array.isArray(savedPair) ? savedPair : [];
      this.syncPair();
    }

    setExpanded(expanded) {
      const button = this.querySelector('[data-compare-expand]'), details = this.querySelector('[data-compare-details]');
      if (!button || !details) return;
      button.setAttribute('aria-expanded', String(expanded));
      const label = node('span', expanded ? this.dataset.collapseSelection : this.dataset.expandSelection), mark = node('span');
      mark.append(icon('down'));
      mark.setAttribute('aria-hidden', 'true');
      button.replaceChildren(label, mark);
      details.hidden = !expanded;
    }

    updateSelection() {
      if (!this.comparisonEnabled) return;
      const feedback = this.querySelector('[data-compare-feedback]');
      if (feedback) feedback.hidden = true;
      this.syncPair();
      storageSet(this.storageKey, this.selected);
      storageSet(this.storageKey + ':pair', this.pair);
      this.cards.forEach(card => {
        const button = card.querySelector('[data-compare-toggle]');
        if (!button) return;
        const selected = this.selected.includes(card.dataset.programId);
        button.setAttribute('aria-pressed', String(selected));
        const label = button.querySelector('[data-compare-label]');
        if (label) label.textContent = selected ? this.dataset.added : this.dataset.add;
        const mark = button.querySelector('[data-compare-icon]');
        if (mark && !mark.querySelector('.uni-compare-check')) mark.replaceChildren(icon(selected ? 'check' : 'plus'));
      });
      const tray = this.querySelector('[data-compare-tray]');
      tray.hidden = this.selected.length === 0;
      if (!this.selected.length) this.setExpanded(false);
      const message = format(this.dataset.selection, { count: this.selected.length, limit: this.limit });
      this.querySelector('[data-compare-count]').textContent = message;
      const selection = this.querySelector('[data-compare-selection]');
      const previousRows = new Map([...selection.children].map(item => [item.dataset.compareRow, item.getBoundingClientRect()]));
      const fragment = document.createDocumentFragment();
      this.selected.forEach(id => {
        const program = this.programs.get(id), item = node('li');
        item.dataset.compareRow = id;
        item.append(node('span', program.shortName || program.name), this.removeButton(program));
        fragment.append(item);
      });
      selection.replaceChildren(fragment);
      if (this.selectionReady && !this.reduced.matches && document.body.dataset.motion !== 'none' && document.body.dataset.motionUser !== 'none' && !selection.closest('[hidden]')) {
        [...selection.children].forEach(item => {
          const previous = previousRows.get(item.dataset.compareRow), current = item.getBoundingClientRect();
          const frames = previous ? [{ transform: `translate(${previous.left - current.left}px,${previous.top - current.top}px)` }, { transform: 'translate(0,0)' }] : [{ opacity: 0, transform: 'translateY(5px)' }, { opacity: 1, transform: 'translateY(0)' }];
          if (typeof item.animate !== 'function') return;
          const animation = item.animate(frames, { duration: 200, easing: 'cubic-bezier(.2,.7,.2,1)' });
          this.animations.add(animation); animation.finished.then(() => this.animations.delete(animation), () => this.animations.delete(animation));
        });
      }
      this.selectionReady = true;
      this.querySelector('[data-compare-open]').disabled = this.selected.length < 2;
      this.announce(message);
      this.renderPairControls();
      if (this.dialog.open) { if (!this.selected.length) this.closeDialog(true); else this.renderComparison(); }
    }

    renderPairControls() {
      const container = this.querySelector('[data-compare-pair]');
      if (!container) return;
      container.hidden = !(this.media.matches && this.selected.length > 2);
      if (container.hidden) { container.replaceChildren(); return; }
      const fieldset = node('fieldset'), legend = node('legend', this.dataset.pairLabel);
      fieldset.append(legend);
      [0, 1].forEach(index => {
        const label = node('label'), select = node('select');
        select.dataset.compareSlot = String(index);
        label.append(node('span', index === 0 ? this.dataset.firstColumn : this.dataset.secondColumn));
        this.selected.forEach(id => {
          const program = this.programs.get(id), option = node('option', program.shortName || program.name);
          option.value = id; select.append(option);
        });
        select.value = this.pair[index]; label.append(select); fieldset.append(label);
      });
      container.replaceChildren(fieldset);
    }

    changePair(index, id) {
      if (![0, 1].includes(index) || !this.selected.includes(id)) return;
      const other = index === 0 ? 1 : 0, previous = this.pair[index];
      if (this.pair[other] === id) this.pair[other] = previous;
      this.pair[index] = id;
      this.syncPair();
      storageSet(this.storageKey + ':pair', this.pair);
      this.querySelectorAll('[data-compare-slot]').forEach(select => { select.value = this.pair[Number(select.dataset.compareSlot)]; });
      this.renderComparison();
    }

    removeButton(program) {
      const button = node('button', undefined, 'uni-compare-remove');
      button.append(icon('close'));
      button.type = 'button'; button.dataset.compareRemove = program.id;
      button.setAttribute('aria-label', format(this.dataset.remove, { name: program.shortName || program.name }));
      return button;
    }

    restoreCardFocus(id) {
      const card = this.cards.find(item => item.dataset.programId === id && !item.hidden);
      (card?.querySelector('[data-compare-toggle]') || this.search)?.focus({ preventScroll: true });
    }

    renderComparison() {
      this.syncPair();
      const ids = this.media.matches ? this.pair : this.selected;
      const programs = ids.map(id => this.programs.get(id)).filter(Boolean);
      const table = node('table', undefined, 'uni-compare-dialog__table');
      table.style.setProperty('--compare-columns', String(programs.length));
      const caption = node('caption', this.querySelector('[data-compare-dialog] h2').textContent, 'uni-sr-only'), head = node('thead'), headRow = node('tr'), corner = node('th', this.dataset.criterionLabel);
      corner.scope = 'col'; headRow.append(corner);
      programs.forEach(program => {
        const cell = node('th'); cell.scope = 'col';
        if (program.area) cell.append(node('span', program.area, 'uni-compare-dialog__area'));
        const link = node('a', program.name, 'uni-compare-dialog__program');
        link.href = program.url; link.dataset.programLink = program.id;
        cell.append(link, this.removeButton(program)); headRow.append(cell);
      });
      head.append(headRow);
      const body = node('tbody');
      [['summary', 'summaryLabel'], ['duration', 'durationLabel'], ['modality', 'modalityLabel'], ['skills', 'skillsLabel'], ['requirements', 'requirementsLabel'], ['occupation', 'occupationLabel']].forEach(([key, label]) => {
        const row = node('tr'), heading = node('th', this.dataset[label]); heading.scope = 'row'; row.append(heading);
        programs.forEach(program => row.append(node('td', program[key] || this.dataset.notConfirmed))); body.append(row);
      });
      const footer = node('tfoot'), footerRow = node('tr'), footerLabel = node('th', this.dataset.openProgram);
      footerLabel.scope = 'row'; footerRow.append(footerLabel);
      programs.forEach(program => {
        const cell = node('td'), link = node('a', this.dataset.openProgram, 'uni-button uni-button--secondary');
        link.href = program.url; link.dataset.programLink = program.id; link.setAttribute('aria-label', this.dataset.openProgram + ': ' + program.name);
        cell.append(link); footerRow.append(cell);
      });
      footer.append(footerRow); table.append(caption, head, body, footer); this.querySelector('[data-compare-content]').replaceChildren(table);
    }

    announce(message) { const status = this.querySelector('[data-compare-status]'); if (status) status.textContent = message; }
  }
  class UniProgram extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const button = this.querySelector('[data-plan-expand]');
      if (!button) return;
      const items = Array.from(this.querySelectorAll('[data-curriculum-items] details'));
      if (!items.length) return;
      button.hidden = false;
      const update = () => {
        const expanded = items.every((item) => item.open);
        button.textContent = expanded ? this.dataset.collapseLabel : this.dataset.expandLabel;
        button.setAttribute('aria-expanded', String(expanded));
      };
      button.addEventListener('click', () => {
        const open = !items.every((item) => item.open);
        items.forEach((item) => { item.open = open; });
        update();
      }, { signal: this.controller.signal });
      items.forEach((item) => item.addEventListener('toggle', update, { signal: this.controller.signal }));
      update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => restoreReturnLinks(), { once: true });
  else restoreReturnLinks();
  document.addEventListener('shopify:section:load', event => restoreReturnLinks(event.target));

  if (!window.customElements.get('uni-catalog')) window.customElements.define('uni-catalog', UniCatalog);
  if (!window.customElements.get('uni-program')) window.customElements.define('uni-program', UniProgram);
})();

